// Captura de audio en el navegador (Bloque C2, paso 3). Nada de esto transcribe, envía el
// audio a ningún servidor, ni lo guarda: vive solo en memoria del navegador mientras la
// pestaña está abierta y se descarta al detener (stopCapture) o al salir de la pantalla.

export type CaptureSource = "mic" | "tab" | "both";

const SOURCE_KEY = "raftel.call_audio_source";

/** Última fuente elegida (localStorage puede fallar: privado, cuota, etc.). */
export const readStoredSource = (): CaptureSource => {
  try {
    const v = localStorage.getItem(SOURCE_KEY);
    return v === "mic" || v === "tab" || v === "both" ? v : "mic";
  } catch {
    return "mic";
  }
};

export const writeStoredSource = (source: CaptureSource) => {
  try {
    localStorage.setItem(SOURCE_KEY, source);
  } catch {
    // no se recuerda la elección; no es motivo para bloquear la captura
  }
};

export class UnsupportedCaptureError extends Error {}
/** Chrome permitió compartir la pestaña/pantalla pero sin marcar "Compartir audio". */
export class NoTabAudioError extends Error {}

/** Nunca deja pistas vivas: para mic, pantalla y la mezcla por igual. */
export const stopMediaStream = (stream: MediaStream | null | undefined) => {
  stream?.getTracks().forEach((t) => t.stop());
};

/** Descarta el video de getDisplayMedia de inmediato: detiene la pista y la quita del stream. */
export const dropVideoTracks = (stream: MediaStream): MediaStream => {
  stream.getVideoTracks().forEach((t) => {
    t.stop();
    stream.removeTrack(t);
  });
  return stream;
};

/** Mezcla varios streams de audio en uno solo (opción "Ambos"), vía Web Audio API. */
export const mixAudioStreams = (
  ctx: AudioContext,
  streams: MediaStream[],
): MediaStream => {
  const dest = ctx.createMediaStreamDestination();
  streams.forEach((s) => ctx.createMediaStreamSource(s).connect(dest));
  return dest.stream;
};

export type CaptureResult = {
  /** El stream final a usar (mic, pantalla, o la mezcla de ambas). */
  stream: MediaStream;
  /** Todo lo que hay que detener al terminar: mic y/o pantalla originales. */
  rawStreams: MediaStream[];
  /** Solo si se mezcló (opción "Ambos"): hay que cerrarlo al terminar. */
  audioContext: AudioContext | null;
};

const requireDisplayMedia = () => {
  if (!navigator.mediaDevices?.getDisplayMedia)
    throw new UnsupportedCaptureError();
};

const captureTab = async (): Promise<MediaStream> => {
  requireDisplayMedia();
  const screen = await navigator.mediaDevices.getDisplayMedia({
    audio: true,
    video: true,
  });
  dropVideoTracks(screen);
  if (!screen.getAudioTracks().length) {
    stopMediaStream(screen);
    throw new NoTabAudioError();
  }
  return screen;
};

/** Arranca la captura para la fuente elegida. Nunca deja un stream a medias si algo falla. */
export const startCapture = async (
  source: CaptureSource,
): Promise<CaptureResult> => {
  if (!navigator.mediaDevices?.getUserMedia)
    throw new UnsupportedCaptureError();

  if (source === "mic") {
    const mic = await navigator.mediaDevices.getUserMedia({ audio: true });
    return { stream: mic, rawStreams: [mic], audioContext: null };
  }

  if (source === "tab") {
    const screen = await captureTab();
    return { stream: screen, rawStreams: [screen], audioContext: null };
  }

  // "both": si la pantalla falla o no comparte audio, no debe quedar el micrófono abierto solo.
  const mic = await navigator.mediaDevices.getUserMedia({ audio: true });
  let screen: MediaStream;
  try {
    screen = await captureTab();
  } catch (e) {
    stopMediaStream(mic);
    throw e;
  }
  const ctx = new AudioContext();
  const mixed = mixAudioStreams(ctx, [mic, screen]);
  return { stream: mixed, rawStreams: [mic, screen], audioContext: ctx };
};

/** Detiene TODO: las pistas originales, la mezcla y el AudioContext. Segura de llamar dos veces. */
export const stopCapture = (result: CaptureResult | null) => {
  if (!result) return;
  result.rawStreams.forEach(stopMediaStream);
  stopMediaStream(result.stream);
  if (result.audioContext && result.audioContext.state !== "closed") {
    void result.audioContext.close();
  }
};

/** Mensaje en español, simple, para cada tipo de error real de estos navegadores. */
export const captureErrorMessage = (e: unknown): string => {
  if (e instanceof NoTabAudioError)
    return 'Chrome no compartió audio. Vuelve a intentar y marca "Compartir audio de la pestaña/sistema".';
  if (e instanceof UnsupportedCaptureError)
    return "Este navegador no soporta capturar audio para el copiloto. Usa Chrome o Edge.";
  if (e instanceof DOMException) {
    if (e.name === "NotAllowedError" || e.name === "SecurityError")
      return "Permiso denegado: acepta el permiso del navegador para poder capturar audio.";
    if (e.name === "NotFoundError")
      return "No se encontró micrófono en este equipo.";
    if (e.name === "NotReadableError")
      return "No se pudo usar el micrófono: revisa que no lo esté usando otro programa u otra pestaña.";
  }
  return "No se pudo iniciar la captura de audio.";
};
